import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AccountRecoveryService } from '../../services/account-recovery.service';

/**
 * Landing page for set-password / reset links (?token=...). Validates the token,
 * lets the user choose a password, then redirects to sign in. Used both for
 * newly provisioned tenant admins and for self-service password resets.
 */
@Component({
  selector: 'app-set-password',
  templateUrl: './set-password.component.html',
  styleUrls: ['./set-password.component.scss'],
})
export class SetPasswordComponent implements OnInit {
  form!: FormGroup;
  token = '';
  checking = true;
  tokenValid = false;
  maskedEmail: string | null = null;
  isSubmitting = false;
  done = false;
  showPassword = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private recovery: AccountRecoveryService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', Validators.required],
      },
      { validators: (g) => (g.get('password')?.value === g.get('confirmPassword')?.value ? null : { mismatch: true }) },
    );

    this.token = this.route.snapshot.queryParamMap.get('token') ?? '';
    if (!this.token) {
      this.checking = false;
      this.tokenValid = false;
      return;
    }

    this.recovery.validateResetToken(this.token).subscribe({
      next: (res) => {
        this.tokenValid = !!res.value?.isValid;
        this.maskedEmail = res.value?.email ?? null;
        this.checking = false;
      },
      error: () => {
        this.tokenValid = false;
        this.checking = false;
      },
    });
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  /** Current password length — drives the strength meter in the template. */
  get pwLen(): number {
    return (this.form?.get('password')?.value ?? '').length;
  }

  /** Strength bucket: '' (too short) | 'fair' | 'good' | 'strong'. */
  get pwStrength(): string {
    const n = this.pwLen;
    if (n < 8) return '';
    if (n >= 14) return 'strong';
    if (n >= 11) return 'good';
    return 'fair';
  }

  /** i18n key for the strength label shown next to the meter. */
  get strengthKey(): string {
    return this.pwStrength === 'strong' ? 'auth.Strong'
         : this.pwStrength === 'good' ? 'auth.Good'
         : 'auth.Fair';
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.recovery.setPassword(this.token, this.form.value.password).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        if (res.isSuccess) {
          this.done = true;
          this.toastr.success('Your password has been set. You can now sign in.');
          setTimeout(() => this.router.navigate(['/account/login']), 2500);
        } else {
          this.toastr.error(res.errors?.[0] ?? 'Could not set your password.');
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        this.toastr.error(err?.error?.errors?.[0]?.messageEn ?? 'Could not set your password.');
      },
    });
  }
}
